import { Reference } from "../types/reference";
import { generateIdentifierUuid } from "./generators";

export interface NoteWithUuid {
  uuid: string;
  author: Reference;
  text: string;
  time: Date;
}

export interface NoteManager {
  createNote: (text: string, author: Reference) => NoteWithUuid;
  updateNote: (note: NoteWithUuid, newText: string) => NoteWithUuid;
  findNoteByText: (
    notes: NoteWithUuid[],
    text: string,
  ) => NoteWithUuid | undefined;
  deduplicateNotesByUuid: (notes: NoteWithUuid[]) => NoteWithUuid[];
}

/**
 * Note Manager for handling notes with UUIDs
 *
 * This utility helps manage notes across multiple patients by:
 * 1. Assigning unique UUIDs to each note
 * 2. Identifying duplicate notes by content
 * 3. Updating existing notes while preserving their UUIDs
 * 4. Removing duplicate UUIDs to prevent rendering issues
 *
 * @example
 * ```typescript
 * // Create a new note
 * const newNote = noteManager.createNote("Patient has diabetes", practitionerReference);
 *
 * // Find an existing note by text
 * const existingNote = noteManager.findNoteByText(notes, "Patient has diabetes");
 *
 * // Update an existing note
 * const updatedNote = noteManager.updateNote(existingNote, "Patient has type 2 diabetes");
 *
 * // Deduplicate notes by UUID
 * const deduplicatedNotes = noteManager.deduplicateNotesByUuid(notes);
 * ```
 */
export const noteManager: NoteManager = {
  /**
   * Creates a new note with a UUID
   * @param text - The note text content
   * @param author - The practitioner reference who created the note
   * @returns A new note with UUID and timestamps
   */
  createNote: (text: string, author: Reference): NoteWithUuid => {
    return {
      uuid: generateIdentifierUuid(),
      text,
      author,
      time: new Date(),
    };
  },

  /**
   * Updates an existing note with new text while preserving the UUID
   * @param note - The existing note to update
   * @param newText - The new text content
   * @returns The updated note with new text and updated timestamp
   */
  updateNote: (note: NoteWithUuid, newText: string): NoteWithUuid => {
    return {
      ...note,
      text: newText,
      time: new Date(),
    };
  },

  /**
   * Finds a note by its text content (for identifying duplicate notes)
   * @param notes - Array of notes to search in
   * @param text - The text content to search for
   * @returns The note with matching text or undefined
   */
  findNoteByText: (
    notes: NoteWithUuid[],
    text: string,
  ): NoteWithUuid | undefined => {
    return notes.find((note) => note.text === text);
  },

  /**
   * Removes duplicate notes by UUID, keeping only the first occurrence
   * @param notes - Array of notes to deduplicate
   * @returns Array of notes with duplicates removed
   */
  deduplicateNotesByUuid: (notes: NoteWithUuid[]): NoteWithUuid[] => {
    const seen = new Set<string>();
    return notes.filter((note) => {
      if (seen.has(note.uuid)) {
        return false;
      }
      seen.add(note.uuid);
      return true;
    });
  },
};
